import moment, {Moment} from "moment";
import {LessonOfTheCourseType} from "./PreviewBooking";
import {GetHolidaysByCompanyAndDateRangeQuery} from "../../../generated/graphql";
import {lightenColor} from "../helper/holidayHelper";
import {blue, orange} from "@ant-design/colors";

export const getDateRangeForAllCourseLessons = (lessonsOfCourse: LessonOfTheCourseType): [Moment | null, Moment | null] => {
    if (!lessonsOfCourse || !Array.isArray(lessonsOfCourse) || lessonsOfCourse.length === 0) {
        return [null, null];
    }

    const sortedLessons = [...lessonsOfCourse].sort((a, b) => moment(a.startDateTime).valueOf() - moment(b.startDateTime).valueOf());

    const firstLesson = sortedLessons[0];
    const lastLesson = sortedLessons[sortedLessons.length - 1];

    return [moment(firstLesson.startDateTime), moment(lastLesson.startDateTime)];
};

export const getCourseLessonsOfTimeRange = (lessonsOfCourse: LessonOfTheCourseType, dateRange: [Moment | null, Moment | null]): LessonOfTheCourseType => {
    if (!lessonsOfCourse || !Array.isArray(lessonsOfCourse) || lessonsOfCourse.length === 0 || !dateRange[0] || !dateRange[1]) {
        console.log("=> getCourseLessonsOfTimeRange: no lessonsOfCourse or dateRange");
        return [];
    }

    const [startDate, endDate] = dateRange;

    return lessonsOfCourse.filter((lesson) => {
        const lessonDate = moment(lesson.startDateTime);
        return lessonDate.isBetween(startDate, endDate, "day", "[]");
    });
};

export const getAllreadyConfirmedTimeRange = (lessonsOfCourse: LessonOfTheCourseType, selectedLessonIds?: Array<string>): [Moment | null, Moment | null] => {
    if (!selectedLessonIds || selectedLessonIds.length === 0) {
        return [null, null];
    }

    const allreadyConfirmedLessons = lessonsOfCourse.filter((lesson) => {
        return selectedLessonIds?.includes(lesson.id ?? "");
    });

    if (!allreadyConfirmedLessons || allreadyConfirmedLessons.length === 0) {
        return [null, null];
    }

    const firstLesson = allreadyConfirmedLessons[0];
    const lastLesson = allreadyConfirmedLessons[allreadyConfirmedLessons.length - 1];

    if (!firstLesson || !lastLesson) {
        return [null, null];
    }

    return [moment(firstLesson.startDateTime), moment(lastLesson.startDateTime)];
};

export const getAlreadyBookedDatesOfAttendee = (lessonsOfCourse: LessonOfTheCourseType, alreadyBookedLessonIdsWithoutCurrentBooking: Array<string>): Array<moment.Moment> => {
    return lessonsOfCourse
        .map((lesson) => {
            if (alreadyBookedLessonIdsWithoutCurrentBooking?.includes(lesson.id ?? "")) {
                return moment(lesson.startDateTime);
            }
            return [];
        })
        .flat();
};

export const getTimeRangeForAllSelectedLessons = (lessonsOfCourse: LessonOfTheCourseType): [Moment | null, Moment | null] => {
    if (!lessonsOfCourse || !Array.isArray(lessonsOfCourse) || lessonsOfCourse.length === 0) {
        return [null, null];
    }

    const sortedLessons = [...lessonsOfCourse].sort((a, b) => moment(a.startDateTime).valueOf() - moment(b.startDateTime).valueOf());

    const firstLesson = sortedLessons[0];
    const lastLesson = sortedLessons[sortedLessons.length - 1];

    return [moment(firstLesson.startDateTime), moment(lastLesson.startDateTime)];
};

export const hasLessonOnDate = (lessonsOfCourse: LessonOfTheCourseType, date: Moment): boolean => {
    if (!lessonsOfCourse || !Array.isArray(lessonsOfCourse) || lessonsOfCourse.length === 0) {
        return false;
    }

    return lessonsOfCourse.some((lesson) => {
        const lessonDate = moment(lesson.startDateTime);
        return lessonDate.isSame(date, "day");
    });
};

export const getHolidayInfo = (date: Moment, holidays: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]) => {
    const allHolidaysOfDate = holidays.filter((holiday) => {
        const holidayStart = moment(holiday.start).startOf("day");
        const holidayEnd = moment(holiday.end).endOf("day");

        return moment(date).isSameOrAfter(holidayStart) && moment(date).isSameOrBefore(holidayEnd);
    });

    return allHolidaysOfDate.find((holiday) => holiday.stateCode === "CUSTOM") ?? allHolidaysOfDate[0];
};

export const getDateBackgroundColor = (current: Moment, lessonsOfCourse: LessonOfTheCourseType, holidays?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]) => {
    const hasLesson = hasLessonOnDate(lessonsOfCourse, current);
    const holiday = getHolidayInfo(current, holidays ?? []);

    // color is a hex color
    // we need to lighten it by 10%
    const lightedHolidayColor = holiday ? lightenColor(holiday.color ?? orange[2], 20) : "transparent";

    if (holiday) {
        const isCustom = holiday.stateCode === "CUSTOM";
        if (isCustom) {
            return holiday.color ?? orange[2];
            // return lightedHolidayColor;
        } else {
            return holiday.holidayStateColor;
            // return lightedHolidayColor;
        }
    } else if (hasLesson) {
        return blue[1];
    }

    return "transparent";
};
